import { db } from '../lib/firebase';
import { collection, query, where, getDocs, orderBy, limit, startAfter, DocumentSnapshot } from 'firebase/firestore';

// Database structure: vindie/{collection}

export interface SearchFilters {
  category?: string;
  genre?: string;
  language?: string;
  uploader?: string;
  contentRating?: string;
  releaseYear?: number;
  runtime?: number;
  tags?: string[];
}

export interface SearchResult {
  id: string;
  title: string;
  description: string;
  category: string;
  genre: string[];
  language: string;
  uploader: string;
  uploaderId: string;
  thumbnail: string;
  views: number;
  likes: number;
  rating: number;
  createdAt: Date;
  contentRating: string;
  releaseYear?: number;
  runtime?: number;
}

export const searchContent = async (
  searchTerm: string,
  filters: SearchFilters = {},
  pageSize: number = 20,
  lastDoc?: DocumentSnapshot
): Promise<{ results: SearchResult[]; lastDoc?: DocumentSnapshot }> => {
  try {
    let q = query(collection(db, 'content'));
    
    // Apply text search (Firestore doesn't support full-text search, so we'll use array-contains for tags)
    if (searchTerm) {
      // Improved search: search by title prefix (case-insensitive)
      // Convert search term to lowercase for better matching
      const searchLower = searchTerm.toLowerCase().trim();
      const searchUpper = searchTerm.charAt(0).toUpperCase() + searchTerm.slice(1).toLowerCase();
      
      // Use prefix matching for better results
      q = query(q, where('title', '>=', searchLower), where('title', '<=', searchLower + '\uf8ff'));
    }
    
    // Apply filters
    if (filters.category) {
      q = query(q, where('category', '==', filters.category));
    }
    
    if (filters.genre) {
      q = query(q, where('genres', 'array-contains', filters.genre));
    }
    
    if (filters.language) {
      q = query(q, where('language', '==', filters.language));
    }
    
    if (filters.uploader) {
      q = query(q, where('uploader', '==', filters.uploader));
    }
    
    if (filters.contentRating) {
      q = query(q, where('contentRating', '==', filters.contentRating));
    }
    
    if (filters.releaseYear) {
      q = query(q, where('releaseYear', '==', filters.releaseYear));
    }
    
    if (filters.tags && filters.tags.length > 0) {
      // Filter by tags using array-contains-any for multiple tags
      q = query(q, where('tags', 'array-contains-any', filters.tags));
    }
    
    // Order by views (most popular first)
    q = query(q, orderBy('views', 'desc'));
    
    // Apply pagination
    if (lastDoc) {
      q = query(q, startAfter(lastDoc));
    }
    
    q = query(q, limit(pageSize));
    
    const querySnapshot = await getDocs(q);
    const results: SearchResult[] = [];
    let newLastDoc: DocumentSnapshot | undefined;
    
    const searchLower = searchTerm.toLowerCase().trim();
    
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      const result = {
        id: doc.id,
        ...data
      } as SearchResult;
      
      // If we have a search term, filter results more strictly
      if (searchTerm) {
        const title = (data.title || '').toLowerCase();
        const description = (data.description || '').toLowerCase();
        const tags = Array.isArray((data as any).tags) ? (data as any).tags.join(' ').toLowerCase() : '';
        const searchText = `${title} ${description} ${tags}`;
        
        // Check if search term appears in title, description, or tags
        if (searchText.includes(searchLower) || title.startsWith(searchLower)) {
          results.push(result);
        }
      } else {
        results.push(result);
      }
      
      newLastDoc = doc;
    });
    
    // Sort results by relevance (exact title matches first, then partial matches)
    if (searchTerm) {
      results.sort((a, b) => {
        const aTitle = (a.title || '').toLowerCase();
        const bTitle = (b.title || '').toLowerCase();
        const searchLower = searchTerm.toLowerCase();
        
        const aExact = aTitle === searchLower ? 1 : 0;
        const bExact = bTitle === searchLower ? 1 : 0;
        if (aExact !== bExact) return bExact - aExact;
        
        const aStarts = aTitle.startsWith(searchLower) ? 1 : 0;
        const bStarts = bTitle.startsWith(searchLower) ? 1 : 0;
        if (aStarts !== bStarts) return bStarts - aStarts;
        
        return (b.views || 0) - (a.views || 0); // Sort by views as tiebreaker
      });
    }
    
    return { results, lastDoc: newLastDoc };
  } catch (error) {
    console.error('Error searching content:', error);
    throw error;
  }
};

export const getTopContent = async (
  category: string,
  timeFrame: 'week' | 'month' | 'year' | 'all' = 'all',
  limitCount: number = 50
): Promise<SearchResult[]> => {
  try {
    let q = query(collection(db, 'content'));
    
    if (category !== 'all') {
      q = query(q, where('category', '==', category));
    }
    
    // Filter by time frame
    const now = new Date();
    let startDate: Date;
    
    switch (timeFrame) {
      case 'week':
        startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
      case 'month':
        startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        break;
      case 'year':
        startDate = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000);
        break;
      default:
        startDate = new Date(0); // All time
    }
    
    if (timeFrame !== 'all') {
      q = query(q, where('createdAt', '>=', startDate));
    }
    
    q = query(q, orderBy('views', 'desc'), limit(limitCount));
    
    const querySnapshot = await getDocs(q);
    const results: SearchResult[] = [];
    
    querySnapshot.forEach((doc) => {
      results.push({
        id: doc.id,
        ...doc.data()
      } as SearchResult);
    });
    
    return results;
  } catch (error) {
    console.error('Error getting top content:', error);
    throw error;
  }
};

export const getContentByGenre = async (
  genre: string,
  limitCount: number = 50
): Promise<SearchResult[]> => {
  try {
    const q = query(
      collection(db, 'content'),
      where('genres', 'array-contains', genre),
      orderBy('views', 'desc'),
      limit(limitCount)
    );
    
    const querySnapshot = await getDocs(q);
    const results: SearchResult[] = [];
    
    querySnapshot.forEach((doc) => {
      results.push({
        id: doc.id,
        ...doc.data()
      } as SearchResult);
    });
    
    return results;
  } catch (error) {
    console.error('Error getting content by genre:', error);
    throw error;
  }
};

export const getContentByTags = async (
  tags: string[],
  limitCount: number = 50
): Promise<SearchResult[]> => {
  try {
    const q = query(
      collection(db, 'content'),
      where('tags', 'array-contains-any', tags),
      orderBy('views', 'desc'),
      limit(limitCount)
    );
    
    const querySnapshot = await getDocs(q);
    const results: SearchResult[] = [];
    
    querySnapshot.forEach((doc) => {
      results.push({
        id: doc.id,
        ...doc.data()
      } as SearchResult);
    });
    
    return results;
  } catch (error) {
    console.error('Error getting content by tags:', error);
    throw error;
  }
};


